import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { TabView, TabPanel } from 'primereact/tabview';
import { Combobox, CustomTable, ETypeOfNotification, Modal, setNotification } from 'medere-ui-framework';
import { getCashRegistersForAdministration, getHandoffDetails, auditHandoffDetail, auditCashClosing } from '../../services/LocationCashController';

import { CashClosingDTO, CashClosingDetailDTO, CashClosingTransferDTO } from '../../dto/CashClosingDTO';
import { CashRegisterDTO, HandoffDetailFlatDTO } from '../../dto/locationcash/LocationCashDTO';
import './CashClosingReportView.scss';
import { getCashClosingDetails, getCashClosings, getCashClosingTransfers } from '../../services/CashClosingController';

interface CashRegisterOption {
    locationCashRegisterId: number;
    hospitalLocationId: number | null;
    description: string;
    displayDescription: string;
}

const AUDIT_STATE_REQUIRES = 2;
const AUDIT_STATE_JUSTIFIED = 3;
const AUDIT_STATE_NOT_JUSTIFIED = 4;

const AUDIT_OPTIONS = [
    { id: AUDIT_STATE_JUSTIFIED, description: 'Justificado' },
    { id: AUDIT_STATE_NOT_JUSTIFIED, description: 'No justificado' },
];

const formatMoney = (value: number | null | undefined) =>
    value == null ? '—' : `$ ${Number(value).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDate = (value: string | null | undefined) =>
    value ? new Date(value).toLocaleString('es-AR') : '—';

function getClosingStatus(closing: CashClosingDTO): { label: string; color: string; icon?: string } {
    if (Math.abs(closing.differenceamount) >= 0.01) {
        return { label: 'Discrepancia', color: '#DC3545', icon: 'pi pi-exclamation-triangle' };
    }
    return { label: 'Sin discrepancia', color: '#28A745' };
}

function getAuditStatusStyle(auditedStateId: number): { color: string; icon?: string } {
    switch (auditedStateId) {
        case 1: return { color: '#5A6676' };
        case 2: return { color: '#E0A800', icon: 'pi pi-clock' };
        case 3: return { color: '#28A745', icon: 'pi pi-check-circle' };
        case 4: return { color: '#DC3545', icon: 'pi pi-times-circle' };
        default: return { color: '#5A6676' };
    }
}

function renderAuditBadge(auditedStateId: number, auditedStateDescription: string) {
    const style = getAuditStatusStyle(auditedStateId);
    return (
        <span style={{ color: style.color }} className="font-semibold">
            {style.icon && <i className={`${style.icon} mr-1`} style={{ fontSize: '12px' }} />}
            {auditedStateDescription}
        </span>
    );
}

export default function CashClosingReportView() {
    const dispatch = useDispatch();

    // Filtro por caja
    const [registers, setRegisters] = useState<CashRegisterOption[]>([]);
    const [selectedRegister, setSelectedRegister] = useState<CashRegisterOption | null>(null);
    const [refresh, setRefresh] = useState(0);

    // Tab activa
    const [activeTab, setActiveTab] = useState(0);

    // ── Cierre: Modal de detalle ──
    const [selectedClosing, setSelectedClosing] = useState<CashClosingDTO | null>(null);
    const [closingModalVisible, setClosingModalVisible] = useState(false);
    const [details, setDetails] = useState<CashClosingDetailDTO[]>([]);
    const [transfers, setTransfers] = useState<CashClosingTransferDTO[]>([]);
    const [loadingDetail, setLoadingDetail] = useState(false);

    // ── Modal de auditoría (compartido) ──
    const [auditModalVisible, setAuditModalVisible] = useState(false);
    const [auditTarget, setAuditTarget] = useState<{ type: 'closing' | 'handoff'; id: number } | null>(null);
    const [auditStateId, setAuditStateId] = useState<number | null>(null);
    const [auditObservations, setAuditObservations] = useState('');
    const [auditing, setAuditing] = useState(false);

    useEffect(() => {
        let cancelled = false;
        getCashRegistersForAdministration()
            .then((data: CashRegisterDTO[]) => {
                if (cancelled) return;
                const options: CashRegisterOption[] = data
                    .filter(r => r.active)
                    .map(r => ({
                        locationCashRegisterId: r.locationCashRegisterId,
                        hospitalLocationId: r.hospitalLocationId ?? null,
                        description: r.description,
                        displayDescription: r.locationDescription
                            ? `${r.description} — ${r.locationDescription}`
                            : r.description,
                    }));
                setRegisters(options);
            })
            .catch(console.error);
        return () => { cancelled = true; };
    }, []);

    // ── Cierre: handlers ──

    async function handleOpenClosingDetail(closing: CashClosingDTO) {
        setSelectedClosing(closing);
        setClosingModalVisible(true);
        setLoadingDetail(true);
        try {
            const [closingDetails, closingTransfers] = await Promise.all([
                getCashClosingDetails(closing.cashclosing),
                getCashClosingTransfers(closing.cashclosing),
            ]);
            setDetails(closingDetails ?? []);
            setTransfers(closingTransfers ?? []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingDetail(false);
        }
    }

    // ── Auditoría ──

    function openAuditModal(type: 'closing' | 'handoff', id: number, currentObservations: string | null) {
        setAuditTarget({ type, id });
        setAuditStateId(null);
        setAuditObservations(currentObservations ?? '');
        setAuditModalVisible(true);
    }

    async function handleAudit() {
        if (!auditTarget || auditStateId == null) return;
        setAuditing(true);
        try {
            const body = { auditedStateId: auditStateId, auditObservations: auditObservations.trim() || undefined };
            if (auditTarget.type === 'closing') {
                await auditCashClosing(auditTarget.id, body);
            } else {
                await auditHandoffDetail(auditTarget.id, body);
            }
            dispatch(setNotification({
                title: 'Auditoría registrada',
                icon: 'pi-check-circle',
                typeOfNotification: ETypeOfNotification.INFO,
            }));
            setAuditModalVisible(false);
            setRefresh(v => v + 1);
        } catch (error: any) {
            dispatch(setNotification({
                title: 'No se pudo registrar la auditoría',
                desc: error?.response?.data?.message,
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
        } finally {
            setAuditing(false);
        }
    }

    const registerId = selectedRegister?.locationCashRegisterId;
    const locationId = selectedRegister?.hospitalLocationId;

    return (
        <div className="cash-closing-report viewContainer">
            <h1 className="ccr-title">Reportes de caja</h1>
            <p className="ccr-subtitle">Cierres de caja y comprobantes de traspaso.</p>

            {/* Filtro por caja */}
            <div className="ccr-filter">
                <div style={{ maxWidth: '350px' }}>
                    <Combobox
                        label="Filtrar por caja"
                        list={registers}
                        value={selectedRegister}
                        setValue={(v: CashRegisterOption | null) => setSelectedRegister(v)}
                        optionLabel="displayDescription"
                        placeholder="Todas las cajas"
                    />
                </div>
            </div>

            <TabView activeIndex={activeTab} onTabChange={e => setActiveTab(e.index)}>
                {/* ══════════ TAB 1: Cierres de caja ══════════ */}
                <TabPanel header="Cierres de caja">
                    <CustomTable
                        key={`closings-${refresh}-${registerId ?? 'all'}`}
                        idTable="tableCashClosingsReport"
                        getItems={(offset: number, pageSize: number) =>
                            getCashClosings(offset, pageSize, registerId || undefined)
                        }
                        filterRow={false}
                        hideFiltersButton
                        showGridlines
                        className="crudTable"
                        notFlex
                    >
                        <Column
                            field="closingdatetime"
                            header="Fecha"
                            body={(row: CashClosingDTO) => formatDate(row.closingdatetime)}
                            style={{ minWidth: '160px' }}
                        />
                        <Column
                            field="closedbyusername"
                            header="Usuario"
                            style={{ minWidth: '120px' }}
                        />
                        <Column
                            field="declaredamount"
                            header="Declarado"
                            body={(row: CashClosingDTO) => formatMoney(row.declaredamount)}
                            style={{ minWidth: '120px' }}
                        />
                        <Column
                            field="expectedamount"
                            header="Real"
                            body={(row: CashClosingDTO) => formatMoney(row.expectedamount)}
                            style={{ minWidth: '120px' }}
                        />
                        <Column
                            header="Estado"
                            body={(row: CashClosingDTO) => {
                                const status = getClosingStatus(row);
                                return (
                                    <span style={{ color: status.color }} className="font-semibold">
                                        {status.icon && <i className={`${status.icon} mr-1`} style={{ fontSize: '12px' }} />}
                                        {status.label}
                                    </span>
                                );
                            }}
                            style={{ minWidth: '130px' }}
                        />
                        <Column
                            header="Auditoría"
                            body={(row: CashClosingDTO) => renderAuditBadge(row.auditedStateId, row.auditedStateDescription)}
                            style={{ minWidth: '160px' }}
                        />
                        <Column
                            field="auditObservations"
                            header="Obs. auditoría"
                            body={(row: CashClosingDTO) => row.auditObservations ?? '—'}
                            style={{ minWidth: '160px' }}
                        />
                        <Column
                            header=""
                            body={(row: CashClosingDTO) => (
                                <div className="flex gap-1">
                                    <Button
                                        icon="pi pi-search"
                                        className="buttonMain3"
                                        tooltip="Ver detalle"
                                        tooltipOptions={{ position: 'left' }}
                                        onClick={() => handleOpenClosingDetail(row)}
                                        style={{ padding: '6px 10px' }}
                                    />
                                    {row.auditedStateId === AUDIT_STATE_REQUIRES && (
                                        <Button
                                            icon="pi pi-check-square"
                                            className="buttonMain2"
                                            tooltip="Auditar"
                                            tooltipOptions={{ position: 'left' }}
                                            onClick={() => openAuditModal('closing', row.cashclosing, row.auditObservations)}
                                            style={{ padding: '6px 10px' }}
                                        />
                                    )}
                                </div>
                            )}
                            style={{ minWidth: '100px', textAlign: 'center' }}
                        />
                    </CustomTable>
                </TabPanel>

                {/* ══════════ TAB 2: Comprobantes de traspaso (lista plana) ══════════ */}
                <TabPanel header="Comprobantes de traspaso">
                    {locationId ? (
                        <CustomTable
                            key={`handoffs-${refresh}-${locationId}`}
                            idTable="tableHandoffDetailsReport"
                            getItems={(offset: number, pageSize: number) =>
                                getHandoffDetails(locationId, offset, pageSize).then(res => res.items)
                            }
                            filterRow={false}
                            hideFiltersButton
                            showGridlines
                            className="crudTable"
                            notFlex
                        >
                            <Column
                                field="signedDatetime"
                                header="Fecha"
                                body={(row: HandoffDetailFlatDTO) => formatDate(row.signedDatetime)}
                                style={{ minWidth: '130px' }}
                            />
                            <Column
                                header="Tipo"
                                body={(row: HandoffDetailFlatDTO) => (
                                    <span className="font-semibold">
                                        <i className={`pi ${row.detailType === 1 ? 'pi-sign-out' : 'pi-sign-in'} mr-1`} style={{ fontSize: '12px' }} />
                                        {row.detailType === 1 ? 'Salida' : 'Recepción'}
                                    </span>
                                )}
                                style={{ minWidth: '100px' }}
                            />
                            <Column
                                field="username"
                                header="Usuario"
                                style={{ minWidth: '90px' }}
                            />
                            <Column
                                header="Declarado"
                                body={(row: HandoffDetailFlatDTO) => formatMoney(row.declaredAmount)}
                                style={{ minWidth: '100px' }}
                            />
                            <Column
                                header="Saldo al firmar"
                                body={(row: HandoffDetailFlatDTO) => formatMoney(row.cashBalanceAtSigning)}
                                style={{ minWidth: '110px' }}
                            />
                            <Column
                                header="Estado"
                                body={(row: HandoffDetailFlatDTO) => {
                                    if (!row.confirmed) {
                                        return <span style={{ color: '#E0A800' }} className="font-semibold"><i className="pi pi-clock mr-1" style={{ fontSize: '12px' }} />Pendiente</span>;
                                    }
                                    if (row.amountDiscrepancy) {
                                        return <span style={{ color: '#DC3545' }} className="font-semibold"><i className="pi pi-exclamation-triangle mr-1" style={{ fontSize: '12px' }} />Discrepancia</span>;
                                    }
                                    return <span style={{ color: '#28A745' }} className="font-semibold">Confirmado</span>;
                                }}
                                style={{ minWidth: '110px' }}
                            />
                            <Column
                                header="Auditoría"
                                body={(row: HandoffDetailFlatDTO) => renderAuditBadge(row.auditedStateId, row.auditedStateDescription)}
                                style={{ minWidth: '130px' }}
                            />
                            <Column
                                field="auditObservations"
                                header="Obs. auditoría"
                                body={(row: HandoffDetailFlatDTO) => row.auditObservations ?? '—'}
                                style={{ minWidth: '120px' }}
                            />
                            <Column
                                header=""
                                body={(row: HandoffDetailFlatDTO) => (
                                    row.auditedStateId === AUDIT_STATE_REQUIRES ? (
                                        <Button
                                            icon="pi pi-check-square"
                                            className="buttonMain2"
                                            tooltip="Auditar"
                                            tooltipOptions={{ position: 'left' }}
                                            onClick={() => openAuditModal('handoff', row.detailId, row.auditObservations)}
                                            style={{ padding: '6px 10px' }}
                                        />
                                    ) : null
                                )}
                                style={{ minWidth: '50px', textAlign: 'center' }}
                            />
                        </CustomTable>
                    ) : (
                        <div className="ccr-empty-state">
                            <i className="pi pi-info-circle" style={{ fontSize: '24px', color: '#5A6676' }} />
                            <p>Seleccione una caja para ver los comprobantes de traspaso.</p>
                        </div>
                    )}
                </TabPanel>
            </TabView>

            {/* ══════════ Modal: Detalle de cierre ══════════ */}
            <Modal
                visible={closingModalVisible}
                setVisible={setClosingModalVisible}
                header="Detalle del cierre de caja"
                width="w-large"
                closable
            >
                {selectedClosing && (() => {
                    const closingStatus = getClosingStatus(selectedClosing);

                    const rowStyle: React.CSSProperties = {
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '8px 0', borderBottom: '1px solid #dee2e6',
                    };
                    const labelStyle: React.CSSProperties = { fontSize: '13px', color: '#5A6676' };
                    const valueStyle: React.CSSProperties = { fontSize: '14px', fontWeight: 600, color: '#1C324F' };
                    const cardStyle: React.CSSProperties = {
                        background: '#f8f9fa', border: '1px solid #dee2e6',
                        borderRadius: '8px', padding: '12px 16px',
                    };
                    const cardTitleStyle: React.CSSProperties = {
                        fontSize: '14px', fontWeight: 600, color: '#1C324F', margin: '0 0 8px',
                    };

                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {/* Info general */}
                            <div>
                                <div style={rowStyle}>
                                    <span style={labelStyle}>Fecha de cierre</span>
                                    <span style={valueStyle}>{formatDate(selectedClosing.closingdatetime)}</span>
                                </div>
                                <div style={rowStyle}>
                                    <span style={labelStyle}>Cerrado por</span>
                                    <span style={valueStyle}>{selectedClosing.closedbyusername}</span>
                                </div>
                                <div style={rowStyle}>
                                    <span style={labelStyle}>Estado</span>
                                    <span style={{ ...valueStyle, color: closingStatus.color }}>
                                        {closingStatus.icon && <i className={`${closingStatus.icon}`} style={{ fontSize: '12px', marginRight: '4px' }} />}
                                        {closingStatus.label}
                                    </span>
                                </div>
                                <div style={{ ...rowStyle, borderBottom: 'none' }}>
                                    <span style={labelStyle}>Auditoría</span>
                                    {renderAuditBadge(selectedClosing.auditedStateId, selectedClosing.auditedStateDescription)}
                                </div>
                                {selectedClosing.auditObservations && (
                                    <div style={{ ...rowStyle, borderBottom: 'none', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                        <span style={labelStyle}>Observaciones de auditoría</span>
                                        <span style={{ fontSize: '13px', color: '#1C324F' }}>{selectedClosing.auditObservations}</span>
                                    </div>
                                )}
                            </div>

                            {/* Montos y Saldos lado a lado */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div style={cardStyle}>
                                    <h4 style={cardTitleStyle}>
                                        <i className="pi pi-wallet" style={{ fontSize: '13px', marginRight: '6px', color: '#5A6676' }} />
                                        Montos
                                    </h4>
                                    <div style={rowStyle}>
                                        <span style={labelStyle}>Real (sistema)</span>
                                        <span style={valueStyle}>{formatMoney(selectedClosing.expectedamount)}</span>
                                    </div>
                                    <div style={rowStyle}>
                                        <span style={labelStyle}>Declarado</span>
                                        <span style={valueStyle}>{formatMoney(selectedClosing.declaredamount)}</span>
                                    </div>
                                    <div style={{ ...rowStyle, borderBottom: 'none' }}>
                                        <span style={labelStyle}>Diferencia</span>
                                        <span style={{
                                            ...valueStyle,
                                            color: Math.abs(selectedClosing.differenceamount) >= 0.01 ? '#DC3545' : valueStyle.color,
                                        }}>
                                            {formatMoney(selectedClosing.differenceamount)}
                                        </span>
                                    </div>
                                </div>

                                <div style={cardStyle}>
                                    <h4 style={cardTitleStyle}>
                                        <i className="pi pi-send" style={{ fontSize: '13px', marginRight: '6px', color: '#5A6676' }} />
                                        Saldos
                                    </h4>
                                    <div style={rowStyle}>
                                        <span style={labelStyle}>Transferido</span>
                                        <span style={valueStyle}>{formatMoney(selectedClosing.transferredamount)}</span>
                                    </div>
                                    <div style={{ ...rowStyle, borderBottom: selectedClosing.observations ? undefined : 'none' }}>
                                        <span style={labelStyle}>Disponible</span>
                                        <span style={valueStyle}>{formatMoney(selectedClosing.remainingamount)}</span>
                                    </div>
                                    {selectedClosing.observations && (
                                        <div style={{ ...rowStyle, borderBottom: 'none', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                            <span style={labelStyle}>Observaciones</span>
                                            <span style={{ fontSize: '13px', color: '#1C324F' }}>{selectedClosing.observations}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Detalle por medio de pago */}
                            <div style={cardStyle}>
                                <h4 style={cardTitleStyle}>
                                    <i className="pi pi-credit-card" style={{ fontSize: '13px', marginRight: '6px', color: '#5A6676' }} />
                                    Detalle por medio de pago
                                </h4>
                                {loadingDetail ? (
                                    <p style={{ color: '#5A6676', fontSize: '13px', margin: 0 }}>Cargando...</p>
                                ) : (
                                    <DataTable value={details} size="small" stripedRows showGridlines emptyMessage="Sin detalles.">
                                        <Column field="paymentmethodname" header="Medio de pago" />
                                        <Column field="expectedamount" header="Real" body={(row: CashClosingDetailDTO) => formatMoney(row.expectedamount)} />
                                        <Column field="declaredamount" header="Declarado" body={(row: CashClosingDetailDTO) => formatMoney(row.declaredamount)} />
                                        <Column
                                            field="differenceamount"
                                            header="Diferencia"
                                            body={(row: CashClosingDetailDTO) => (
                                                <span style={Math.abs(row.differenceamount) >= 0.01 ? { color: '#DC3545' } : undefined}>
                                                    {formatMoney(row.differenceamount)}
                                                </span>
                                            )}
                                        />
                                    </DataTable>
                                )}
                            </div>

                            {/* Transferencias registradas */}
                            <div style={cardStyle}>
                                <h4 style={cardTitleStyle}>
                                    <i className="pi pi-arrow-right-arrow-left" style={{ fontSize: '13px', marginRight: '6px', color: '#5A6676' }} />
                                    Transferencias registradas
                                </h4>
                                {loadingDetail ? (
                                    <p style={{ color: '#5A6676', fontSize: '13px', margin: 0 }}>Cargando...</p>
                                ) : (
                                    <DataTable value={transfers} size="small" stripedRows showGridlines emptyMessage="Sin transferencias.">
                                        <Column field="transferdatetime" header="Fecha" body={(row: CashClosingTransferDTO) => formatDate(row.transferdatetime)} />
                                        <Column field="amount" header="Monto" body={(row: CashClosingTransferDTO) => formatMoney(row.amount)} />
                                        <Column field="sourcefinancialaccountname" header="Cuenta origen" body={(row: CashClosingTransferDTO) => row.sourcefinancialaccountname ?? '—'} />
                                        <Column field="destinationfinancialaccountname" header="Cuenta destino" body={(row: CashClosingTransferDTO) => row.destinationfinancialaccountname ?? '—'} />
                                        <Column field="transferredbyusername" header="Registrado por" />
                                        <Column field="observations" header="Observaciones" body={(row: CashClosingTransferDTO) => row.observations ?? '—'} />
                                    </DataTable>
                                )}
                            </div>
                        </div>
                    );
                })()}
            </Modal>

            {/* ══════════ Modal: Auditoría ══════════ */}
            <Modal
                visible={auditModalVisible}
                setVisible={setAuditModalVisible}
                header="Auditar registro"
                width="w-medium"
                closable={!auditing}
            >
                <div className="flex flex-column gap-3">
                    <p className="m-0" style={{ color: '#5A6676', fontSize: '13px' }}>
                        Seleccione el resultado de la auditoría e ingrese observaciones si corresponde.
                    </p>
                    <div className="flex flex-column gap-1">
                        <label className="font-medium" style={{ fontSize: '13px' }}>Resultado de auditoría *</label>
                        <Dropdown
                            value={auditStateId}
                            options={AUDIT_OPTIONS}
                            optionLabel="description"
                            optionValue="id"
                            placeholder="Seleccionar resultado..."
                            onChange={e => setAuditStateId(e.value)}
                            disabled={auditing}
                            className="w-full"
                        />
                    </div>
                    <div className="flex flex-column gap-1">
                        <label className="font-medium" style={{ fontSize: '13px' }}>Observaciones de auditoría</label>
                        <InputTextarea
                            value={auditObservations}
                            onChange={e => setAuditObservations(e.target.value)}
                            rows={3}
                            autoResize
                            placeholder="Ingrese observaciones..."
                            disabled={auditing}
                        />
                    </div>
                    <div className="flex justify-content-end gap-2 mt-2">
                        <Button
                            label="Cancelar"
                            className="buttonMain3"
                            onClick={() => setAuditModalVisible(false)}
                            disabled={auditing}
                        />
                        <Button
                            label="Confirmar auditoría"
                            icon="pi pi-check"
                            className="buttonMain"
                            onClick={handleAudit}
                            loading={auditing}
                            disabled={auditStateId == null}
                        />
                    </div>
                </div>
            </Modal>
        </div>
    );
}
